/**
 * Zod schema for the Schedule section. Mirrors `ScheduleSection` in
 * types/submission.ts. Validation messages surface in field tooltips.
 */
import { z } from "zod";
import { INVALID_DATE_MESSAGE, isEmptyOrValidUsDate } from "../../../utils/date-validation";

/** Date helpers — accept blank or a real MM/DD/YYYY calendar date. */
const optionalDate = z.string().refine(isEmptyOrValidUsDate, INVALID_DATE_MESSAGE);

export const relativitySchema = z.object({
    skilled: z.string(),
    assisted: z.string(),
    independent: z.string(),
    additionalExposure: z.string(),
});

export const facilityRowSchema = z.object({
    id: z.string(),
    // No field on a facility row is required — a row can be added and
    // saved with any subset of fields filled in (incl. none). Dates,
    // when present, must still be a valid MM/DD/YYYY calendar date.
    facilityName: z.string(),
    /** State / federal provider number — free text, optional. Sits
     *  between Facility Name and Acquired in the table. */
    providerNumber: z.string(),
    acquired: optionalDate,
    operator: z.string(),
    address: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    geo: z.string(), // "Rural" | "Urban" | ""
    county: z.string(),
    plRetro: optionalDate,
    glRetro: optionalDate,
    retention: z.string(), // numeric string, picked via MoneySelect
    plLimit: z.string(),
    glLimit: z.string(),
    occupancy: z.string(), // %
    skilled: z.string(),
    assisted: z.string(),
    independent: z.string(),
    addlExposure: z.string(),
    rate: z.string(), // $
    unadjPrem: z.string(),
    matCredit: z.string(), // %
    matAdjPrem: z.string(),
    sirCredit: z.string(), // %
    sirAdjPrem: z.string(),
    licSnf: z.string(),
    licAlf: z.string(),
    licIlf: z.string(),
});

// Read-only, auto-derived from the Facility Schedule (see
// `useScheduleAggregates`). Nothing here is user-entered, and a state with
// zero exposure legitimately emits an empty `exposure` string — so NONE of
// these are `required()`. (They were when the table was hand-editable;
// keeping the requirement silently blocked Save once it went read-only,
// because the hidden validation error has no visible field to surface on.)
export const stateDistributionRowSchema = z.object({
    id: z.string(),
    state: z.string(),
    exposure: z.string(),
    rates: z.string(),
});

export const scheduleSchema = z.object({
    rateUnadjusted: z.string(),
    rateAdjustedOccupancy: z.string(),
    relativity: relativitySchema,
    facilities: z.array(facilityRowSchema),
    stateDistribution: z.array(stateDistributionRowSchema),
});

export type ScheduleFormData = z.infer<typeof scheduleSchema>;

let rowIdCounter = 0;
function createRowId(prefix: string): string {
    rowIdCounter += 1;
    return `${prefix}-new-${Date.now()}-${rowIdCounter}`;
}

export const emptyFacilityRow = (): z.infer<typeof facilityRowSchema> => ({
    id: createRowId("fac"),
    facilityName: "",
    providerNumber: "",
    acquired: "",
    operator: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    geo: "",
    county: "",
    plRetro: "",
    glRetro: "",
    retention: "",
    plLimit: "",
    glLimit: "",
    occupancy: "",
    skilled: "",
    assisted: "",
    independent: "",
    addlExposure: "",
    rate: "",
    unadjPrem: "",
    matCredit: "",
    matAdjPrem: "",
    sirCredit: "",
    sirAdjPrem: "",
    licSnf: "",
    licAlf: "",
    licIlf: "",
});

export const emptyStateDistributionRow = (): z.infer<typeof stateDistributionRowSchema> => ({
    id: createRowId("sd"),
    state: "",
    exposure: "",
    rates: "",
});
